import React, { useEffect, useMemo } from "react";
import { Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { unstable_useBlocker, useLoaderData } from "react-router-dom";

import { yupResolver } from "@hookform/resolvers/yup";

import { IGame, IPlayer, IUmpireRating } from "@src/types/api";

import { alertActions } from "@features/alert/alertSlice";

import { useAppDispatch } from "@hooks/reduxHooks";

import GameDetails from "@Clubhouse/components/GameSummary/components/GameDetails";
import GameSummaryForm from "@Clubhouse/components/GameSummary/components/GameSummaryForm";
import { GameSummaryProvider } from "@Clubhouse/components/GameSummary/contexts/GameSummary";
import gameSummarySchema from "@Clubhouse/components/GameSummary/lib/gameSummarySchema";
import { IGameSummaryData } from "@Clubhouse/components/GameSummary/types/gamesummary";

const GameSummary = (): React.JSX.Element => {
    const { game, umpireRatings, homeRoster, visitorRoster } =
        useLoaderData() as {
            game: IGame;
            umpireRatings: IUmpireRating[];
            homeRoster: IPlayer[];
            visitorRoster: IPlayer[];
        };

    const dispatch = useAppDispatch();

    const plateUmpireRatings = umpireRatings.find(
        r =>
            parseInt(r.umpire.id.toString()) ===
            game.umpires?.find(u => u.location === "plate")?.id,
    );
    const fieldUmpireRatings = umpireRatings.find(
        r =>
            parseInt(r.umpire.id.toString()) ===
            game.umpires?.find(u => u.location === "field")?.id,
    );

    const methods = useForm<Partial<IGameSummaryData>>({
        mode: "onChange",
        resolver: yupResolver(gameSummarySchema),
        defaultValues: {
            plate: {
                attitude: plateUmpireRatings?.ratings.attitude || null,
                consistency: plateUmpireRatings?.ratings.consistency || null,
                effort: plateUmpireRatings?.ratings.effort || null,
                overall: plateUmpireRatings?.ratings.overall || null,
                comments: plateUmpireRatings?.comments || null,
            },
            field: {
                attitude: fieldUmpireRatings?.ratings.attitude || null,
                effort: fieldUmpireRatings?.ratings.effort || null,
                overall: fieldUmpireRatings?.ratings.overall || null,
                comments: fieldUmpireRatings?.comments || null,
            },
            scores: {
                home: game.home.score || null,
                visitor: game.visitor.score || null,
            },
            pitchCounts: {
                home: game.home.pitchCounts?.map(p => ({
                    count: p.count,
                    player: homeRoster
                        .filter(r => r.id === p.player)
                        .map(p => {
                            return {
                                value: p.id,
                                label: `${p.firstName} ${p.lastName}`,
                            };
                        })[0],
                })),
                visitor: game.visitor.pitchCounts?.map(p => ({
                    count: p.count,
                    player: visitorRoster
                        .filter(r => r.id === p.player)
                        .map(p => {
                            return {
                                value: p.id,
                                label: `${p.firstName} ${p.lastName}`,
                            };
                        })[0],
                })),
            },
            homeSummary: game.home.summary,
            visitorSummary: game.visitor.summary,
        },
    });

    const dirtyFieldsCount = useMemo(
        () => Object.keys(methods.formState.dirtyFields).length,
        [methods.formState.dirtyFields],
    );

    const blocker = unstable_useBlocker(
        ({ currentLocation, nextLocation }) =>
            dirtyFieldsCount > 0 &&
            currentLocation.pathname !== nextLocation.pathname,
    );

    useEffect(() => {
        blocker.state === "blocked" &&
            dispatch(
                alertActions.warning({
                    dismissible: false,
                    title: "You have unsaved changes on this page!",
                    text: (
                        <>
                            Any unsaved changes will be lost. If you wish to
                            submit your changes, be sure to click "Update Game
                            Summary" at the bottom of the page before leaving.
                            <br />
                            Do you wish to leave this page?
                            <br />
                            <Button
                                type="button"
                                variant="primary"
                                onClick={() => {
                                    dispatch(alertActions.clear());
                                    blocker.reset();
                                }}
                                style={{ marginRight: "1em" }}
                            >
                                No
                            </Button>
                            <Button
                                type="button"
                                variant="danger"
                                onClick={() => {
                                    dispatch(alertActions.clear());
                                    blocker.proceed();
                                }}
                            >
                                Yes
                            </Button>
                        </>
                    ),
                }),
            );
    }, [blocker]);

    return (
        <section data-testid="gameSummaryContent">
            <GameSummaryProvider
                game={game}
                umpireRatings={umpireRatings}
                methods={methods}
                homeRoster={homeRoster}
                visitorRoster={visitorRoster}
            >
                <GameDetails />
                <GameSummaryForm />
            </GameSummaryProvider>
        </section>
    );
};

export default GameSummary;
